/**
 * Corpus — WHAT IS IN FORCE, and RETIRE.
 *
 * This screen is the reason the platform door needed an eleventh route. The ten
 * that existed could show an operator the proposal QUEUE, and a proposal
 * freezes at `published` forever — so a console built on the queue alone would
 * render a withdrawn requirement as "published" and invite an operator to
 * supersede a floor that stopped applying in March.
 *
 * Every row here therefore states its lifecycle in a SENTENCE, not a badge, and
 * supersession is shown in both directions because `supersedes_id` points only
 * backwards.
 */
import { useCallback, useState } from 'react';
import type { ConsoleClient } from '../platform/client.js';
import type { PlatformActor } from '../platform/credentials.js';
import * as requests from '../platform/requests.js';
import type { Requirement } from '../platform/types.js';
import {
  describeLifecycle,
  describeProvenance,
  describeRetirement,
  describeSupersession,
  describeVersionLabel,
  describeWindow,
  retirementAvailability,
} from '../platform/lifecycle.js';
import { Outcome } from './Outcome.js';
import type { ActResult } from './Outcome.js';

export function Corpus({
  corpus,
  actor,
  client,
  onChanged,
}: {
  corpus: Requirement[] | null;
  actor: PlatformActor;
  client: ConsoleClient;
  onChanged: () => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = corpus?.find((r) => r.id === selectedId) ?? null;

  return (
    <section>
      <h2>Published corpus</h2>
      {corpus === null ? (
        <p className="quiet">
          WITHHELD — the corpus did not load. Nothing below should be read as "there are no
          published requirements".
        </p>
      ) : corpus.length === 0 ? (
        <p className="quiet">
          No external requirements have ever been published. A measured zero across every state, not
          a filter.
        </p>
      ) : (
        <div className="row">
          <div className="col">
            {corpus.map((r) => (
              <button
                key={r.id}
                type="button"
                className="card"
                data-selected={r.id === selectedId}
                style={{ display: 'block', width: '100%', textAlign: 'left' }}
                onClick={() => setSelectedId(r.id === selectedId ? null : r.id)}
              >
                <h3>{r.title}</h3>
                <p className="quiet">
                  {r.subject} · {r.jurisdiction ?? 'no jurisdiction recorded'} · {r.bindingness}
                </p>
                <p className="quiet">{describeLifecycle(r)}</p>
              </button>
            ))}
          </div>
          <div className="col">
            {selected ? (
              <RequirementDetail
                requirement={selected}
                actor={actor}
                client={client}
                onChanged={onChanged}
              />
            ) : (
              <p className="quiet">Select a requirement.</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function RequirementDetail({
  requirement,
  actor,
  client,
  onChanged,
}: {
  requirement: Requirement;
  actor: PlatformActor;
  client: ConsoleClient;
  onChanged: () => void;
}) {
  const [effectiveTo, setEffectiveTo] = useState('');
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<ActResult | null>(null);
  const availability = retirementAvailability(requirement);

  const retire = useCallback(async () => {
    setResult(null);
    const outcome = await client<unknown>(
      requests.retire(requirement.id, effectiveTo, reason),
      actor,
    );
    setResult({ label: 'Withdrawal', outcome });
    if (outcome.kind === 'ok') onChanged();
  }, [client, actor, requirement.id, effectiveTo, reason, onChanged]);

  return (
    <div className="card">
      <h3>{requirement.title}</h3>
      <dl>
        <dt>Lifecycle</dt>
        <dd>{describeLifecycle(requirement)}</dd>
        <dt>Effective dates</dt>
        <dd>{describeWindow(requirement.effectiveFrom, requirement.effectiveTo)}</dd>
        <dt>Version</dt>
        <dd>{describeVersionLabel(requirement.versionLabel)}</dd>
        <dt>Version history</dt>
        <dd>{describeSupersession(requirement)}</dd>
        <dt>Withdrawal</dt>
        <dd>{describeRetirement(requirement)}</dd>
        <dt>Source</dt>
        <dd>
          {requirement.publisher} · {requirement.authority} · {requirement.sourceClass}
          <div className="quiet">
            {requirement.citation ?? 'No citation recorded.'}{' '}
            {requirement.referenceUrl ?? 'No reference URL recorded.'}
          </div>
        </dd>
        <dt>Provenance</dt>
        <dd>{describeProvenance(requirement)}</dd>
        <dt>Applicability</dt>
        <dd>
          {[
            requirement.jurisdiction ? `jurisdiction ${requirement.jurisdiction}` : null,
            requirement.programKey ? `program ${requirement.programKey}` : null,
            requirement.serviceKey ? `service ${requirement.serviceKey}` : null,
            requirement.payerKey ? `payer ${requirement.payerKey}` : null,
            requirement.roleKey ? `role ${requirement.roleKey}` : null,
          ]
            .filter(Boolean)
            .join(' · ') || 'No applicability keys recorded.'}
        </dd>
      </dl>

      <h3>Withdraw this version</h3>
      {availability.available ? (
        <>
          <p className="quiet">
            Withdrawal is atomic and permanent: the state, the closing date and the attribution move
            in one statement, and the version itself is never deleted or rewritten. An as-of BEFORE
            the closing day still resolves to it, because it really was in force then.
          </p>
          <label htmlFor="effectiveTo">
            The day it stopped applying (YYYY-MM-DD; not defaulted to today)
          </label>
          <input
            id="effectiveTo"
            value={effectiveTo}
            onChange={(e) => setEffectiveTo(e.target.value)}
            placeholder="2026-04-01"
          />
          <label htmlFor="reason">Why it is no longer the floor</label>
          <textarea
            id="reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <p>
            <button
              type="button"
              disabled={!effectiveTo.trim() || !reason.trim()}
              onClick={() => void retire()}
            >
              Withdraw
            </button>
          </p>
          {!effectiveTo.trim() || !reason.trim() ? (
            <p className="quiet">
              Both are required and neither is defaulted. A repeal has a date and it is rarely
              today; a default would put a date in the audit trail that nobody assigned.
            </p>
          ) : null}
        </>
      ) : (
        <p className="quiet">{availability.because}</p>
      )}

      <Outcome result={result} />
    </div>
  );
}
